import { cn, NonTimezoneSensitiveDate } from '@commenda/utils';
import { Calendar as CalendarIcon } from 'lucide-react';
import * as React from 'react';
import { DateRange } from 'react-day-picker';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Button } from './radix-button';

export function DateRangePicker({
  date,
  setDate,
  className = '',
  renderTrigger,
  disabled = false,
}: {
  className?: string;
  date: DateRange;
  setDate: (date: DateRange) => void;
  renderTrigger?: (props: {
    date: DateRange;
    isOpen: boolean;
    onClick: () => void;
  }) => React.ReactNode;
  disabled?: boolean;
}) {
  const [isOpen, setIsOpen] = React.useState(false);

  const handlePopoverOpenChange = (open: boolean) => {
    if (disabled) return;
    setIsOpen(open);
  };

  const handleCalendarSelect = (selectedRange: DateRange | undefined) => {
    if (!selectedRange?.from) return;

    const completeRange: DateRange = {
      from: selectedRange.from,
      to: selectedRange.to || selectedRange.from,
    };

    setDate(completeRange);
  };

  return (
    <Popover open={isOpen} onOpenChange={handlePopoverOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        {renderTrigger ? (
          renderTrigger({
            date,
            isOpen,
            onClick: () => handlePopoverOpenChange(true),
          })
        ) : (
          <Button
            id="date"
            variant="outline"
            className={cn(
              'w-[300px] justify-start text-left font-normal disabled:cursor-not-allowed',
              !date && 'text-muted-foreground'
            )}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {NonTimezoneSensitiveDate.fromNativeDate(date.from).format(
                    'LLL dd, y'
                  )}{' '}
                  -{' '}
                  {NonTimezoneSensitiveDate.fromNativeDate(date.to).format(
                    'LLL dd, y'
                  )}
                </>
              ) : (
                NonTimezoneSensitiveDate.fromNativeDate(date.from).format(
                  'LLL dd, y'
                )
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          initialFocus
          mode="range"
          defaultMonth={date?.from}
          selected={date}
          onSelect={handleCalendarSelect}
          numberOfMonths={2}
        />
      </PopoverContent>
    </Popover>
  );
}
